import { Link, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useReveal } from '@/lib/useReveal';
import useDocumentMeta from '@/lib/useDocumentMeta';
import { getExhibition, getVideos } from '@/lib/api';
import { VideoThumb } from './Videos';
import StructuredData, { exhibitionLd } from '@/components/StructuredData';
import PictureImg from '@/components/PictureImg';
import { getArtworkAspectRatio } from '@/lib/utils';

const fmt = (s) => new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
const STATUS_LABEL = { current: 'On view', upcoming: 'Upcoming', past: 'Past' };

/**
 * InstallationGallery — a manually-curated image gallery for exhibitions.
 * Displays ONLY the images the gallery owner uploads via
 * `Admin → Exhibitions → Exhibition Gallery Images`. No auto-population,
 * no auto captions, no hero duplication. Layout rhythm:
 *   • Image 1  → full-width hero of the gallery
 *   • Remaining images alternate paired / single so portrait and landscape
 *     photographs sit naturally together without cropping.
 * A default aspect ratio is reserved via `.img-wrap--natural` (no per-image
 * dimension metadata exists for manually-uploaded exhibition photos).
 */
function InstallationGallery({ images, title }) {
    const list = (images || []).filter(Boolean);
    if (list.length === 0) return null;
    /* Compose a sequence of rows.
       Row A = one image (full-width). Row B = two images side-by-side.
       Distribution: first image alone, then alternate B / A / B / A …
       For counts of 1 or 2, keep it simple (all single-row).            */
    const rows = [];
    let i = 0;
    if (list[i] != null) { rows.push([list[i]]); i += 1; }
    let takePair = true;
    while (i < list.length) {
        if (takePair && i + 1 < list.length) {
            rows.push([list[i], list[i + 1]]);
            i += 2;
        } else {
            rows.push([list[i]]);
            i += 1;
        }
        takePair = !takePair;
    }
    /* Running index so alt="view N" is monotonic and unique regardless of
       how the rows split (avoids duplicates when there are ≥ 4 images). */
    let altN = 0;
    return (
        <div className="flex flex-col gap-20 md:gap-28" data-testid="installation-gallery">
            {rows.map((row, ri) => (
                row.length === 1 ? (
                    (() => { altN += 1; const n = altN; return (
                        <figure key={ri} className="reveal" data-testid={`installation-figure-${ri}`}>
                            <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)' }}>
                                <PictureImg src={row[0]} alt={`${title} — view ${n}`} loading="lazy" />
                            </div>
                        </figure>
                    ); })()
                ) : (
                    <div key={ri} className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
                        {row.map((src, ci) => {
                            altN += 1;
                            const n = altN;
                            return (
                                <figure key={ci} className="reveal" data-testid={`installation-figure-${ri}-${ci}`}>
                                    <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)' }}>
                                        <PictureImg src={src} alt={`${title} — view ${n}`} loading="lazy" />
                                    </div>
                                </figure>
                            );
                        })}
                    </div>
                )
            ))}
        </div>
    );
}

export default function ExhibitionDetail() {
    const { slug } = useParams();
    const reveal = useReveal();
    const [data, setData] = useState(null);
    const [films, setFilms] = useState([]);
    useEffect(() => {
        getExhibition(slug).then(setData);
        getVideos({ exhibition: slug }).then(setFilms);
    }, [slug]);

    useDocumentMeta({
        title: data ? `${data.exhibition.title} · Maison Touré` : 'Exhibition · Maison Touré',
        description: data ? `${data.exhibition.subtitle}. ${data.exhibition.description?.slice(0, 140)}` : undefined,
    });

    if (!data) {
        return (
            <div style={{ paddingTop: 200 }} className="max-w-[1600px] mx-auto px-8 md:px-16">
                <p className="serif-italic" style={{ fontSize: 22, color: 'var(--ink-2)' }}>Loading…</p>
            </div>
        );
    }
    const { exhibition, artists, artworks } = data;

    return (
        <div ref={reveal} data-testid="exhibition-detail-page">
            <StructuredData data={exhibitionLd(exhibition)} />
            {/* HERO */}
            <section className="relative h-[88vh] overflow-hidden">
                <div className="absolute inset-0 hero-fade">
                    <PictureImg
                        src={exhibition.cover_image}
                        alt={exhibition.title}
                        className="w-full h-full object-cover"
                        style={{ filter: 'brightness(0.88) contrast(1.03)' }}
                    />
                </div>
                <div className="relative z-10 h-full flex items-end">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 pb-20 w-full">
                        <span className="overline" style={{ color: 'var(--bg)' }}>
                            {STATUS_LABEL[exhibition.status]}
                        </span>
                        <h1
                            className="mt-6"
                            style={{
                                color: 'var(--bg)',
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(46px, 6.6vw, 106px)',
                                lineHeight: 0.98,
                                letterSpacing: '-0.025em',
                                textShadow: '0 1px 24px rgba(20,18,15,0.32)',
                            }}
                        >
                            <em className="serif-italic" style={{ fontWeight: 500 }}>{exhibition.title}</em>
                        </h1>
                        <p
                            className="mt-5"
                            style={{
                                color: 'rgba(249,248,246,0.92)',
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontStyle: 'italic',
                                fontSize: 'clamp(20px, 2vw, 28px)',
                            }}
                        >
                            {exhibition.subtitle}
                        </p>
                    </div>
                </div>
            </section>

            {/* CURATORIAL INTRO */}
            <section className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-3">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>I &nbsp;—&nbsp; The Exhibition</span>
                        <div
                            className="mt-10 overline"
                            style={{ color: 'var(--ink-3)', lineHeight: 1.8 }}
                        >
                            {fmt(exhibition.start_date)}<br />
                            {fmt(exhibition.end_date)}<br />
                            <span style={{ textTransform: 'none', letterSpacing: 0, fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: 14 }}>
                                {exhibition.location}
                            </span>
                        </div>
                    </div>
                    <div className="md:col-span-8 md:col-start-5 reveal">
                        <p
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 'clamp(22px, 2.2vw, 32px)',
                                lineHeight: 1.42,
                                letterSpacing: '-0.008em',
                                color: 'var(--ink)',
                            }}
                        >
                            {exhibition.description}
                        </p>
                    </div>
                </div>
            </section>

            {/* GALLERY — manually-curated images per exhibition. Only renders
                what the gallery owner explicitly uploads via
                Admin → Exhibitions → Exhibition Gallery Images. No automatic
                pulls from artworks or the hero. If empty, the section is
                fully hidden. Captions are intentionally omitted. */}
            {Array.isArray(exhibition.gallery_images) && exhibition.gallery_images.length > 0 && (
                <section className="rule-top" data-testid="exhibition-installation">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-20 md:mb-28">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>II &nbsp;—&nbsp; Gallery</span>
                        </header>
                        <InstallationGallery
                            images={exhibition.gallery_images}
                            title={exhibition.title}
                        />
                    </div>
                </section>
            )}

            {/* ARTISTS */}
            {artists.length > 0 && (
                <section className="rule-top">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16 md:mb-20">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>III &nbsp;—&nbsp; Featured Artists</span>
                        </header>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-10 gap-y-16">
                            {artists.map((a, i) => (
                                <Link key={a.slug} to={`/artists/${a.slug}`} className={`reveal group block ${i % 2 === 1 ? 'md:mt-12' : ''}`}>
                                    <div className="img-wrap aspect-[3/4]" style={{ background: 'var(--bg-3)' }}>
                                        <PictureImg src={a.portrait} alt={a.name} loading="lazy" />
                                    </div>
                                    <h3
                                        className="mt-6"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontWeight: 400,
                                            fontSize: 22,
                                            lineHeight: 1.16,
                                            letterSpacing: '-0.012em',
                                        }}
                                    >
                                        {a.name}
                                    </h3>
                                    <div
                                        className="mt-1"
                                        style={{
                                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                                            fontStyle: 'italic',
                                            fontSize: 14,
                                            color: 'var(--ink-3)',
                                        }}
                                    >
                                        {a.origin}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ARTWORKS */}
            {artworks.length > 0 && (
                <section className="rule-top">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16 md:mb-20">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>IV &nbsp;—&nbsp; Works in the Exhibition</span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-20">
                            {artworks.map((w, i) => (
                                <Link key={w.slug} to={`/artworks/${w.slug}`} className={`reveal group block ${i % 3 === 1 ? 'md:mt-16' : ''}`}>
                                    <div className="img-wrap img-wrap--natural" style={{ background: 'var(--bg-3)', aspectRatio: getArtworkAspectRatio(w) }} onContextMenu={(e) => e.preventDefault()}>
                                        <PictureImg src={w.image} alt={w.title} loading="lazy" className="no-save" />
                                    </div>
                                    <div className="mt-7">
                                        <div
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontSize: 16,
                                                color: 'var(--ink-2)',
                                            }}
                                        >
                                            {w.artist_name}
                                        </div>
                                        <h3
                                            className="mt-2"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(22px, 2.2vw, 30px)',
                                                lineHeight: 1.12,
                                                letterSpacing: '-0.012em',
                                            }}
                                        >
                                            {w.title}, <span style={{ color: 'var(--ink-3)' }}>{w.year}</span>
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FILMS — Exhibition vernissages, walkthroughs, conversations */}
            {films.length > 0 && (
                <section className="rule-top" data-testid="exhibition-films">
                    <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48">
                        <header className="reveal mb-16">
                            <span className="overline" style={{ color: 'var(--ink-2)' }}>From the Film Archive</span>
                        </header>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-16">
                            {films.map((v) => (
                                <Link to={`/film/${v.slug}`} key={v.slug} className="reveal group block">
                                    <VideoThumb video={v} />
                                    <div className="mt-6">
                                        <span className="overline" style={{ color: 'var(--ink-2)' }}>{v.category}</span>
                                        <h3
                                            className="mt-3"
                                            style={{
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontStyle: 'italic',
                                                fontWeight: 500,
                                                fontSize: 'clamp(22px, 2vw, 28px)',
                                                lineHeight: 1.18,
                                                letterSpacing: '-0.01em',
                                            }}
                                        >
                                            {v.title}
                                        </h3>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* VISITOR INFORMATION */}
            <section className="rule-top">
                <div className="max-w-[1600px] mx-auto px-8 md:px-16 lg:px-20 py-32 md:py-48 grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-3">
                        <span className="overline" style={{ color: 'var(--ink-2)' }}>V &nbsp;—&nbsp; Visitor Information</span>
                    </div>
                    <div className="md:col-span-8 md:col-start-5 reveal">
                        <p
                            style={{
                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                fontWeight: 400,
                                fontSize: 18,
                                lineHeight: 1.75,
                                color: 'var(--ink-2)',
                            }}
                        >
                            Maison Touré is opened by appointment only. To arrange a private viewing of <em className="serif-italic">{exhibition.title}</em>, please write to the gallery or request a viewing online.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-12">
                            <Link to="/private-viewing" className="btn-ghost">Request a private viewing</Link>
                            <Link to="/contact" className="btn-ghost">Contact the gallery</Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
